import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, PartyPopper } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentDistributionPanel } from '@/components/workflows/DocumentDistributionPanel';
import { GovernanceFilingPanel } from '@/components/workflows/GovernanceFilingPanel';

interface MemberPhase3Props {
  taskId: string;
  unaId: string;
  task: any;
  phaseData: any;
  onPhaseComplete?: () => void;
}

export function MemberPhase3({ taskId, unaId, task, phaseData, onPhaseComplete }: MemberPhase3Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [distributionComplete, setDistributionComplete] = useState(false);
  const [filingComplete, setFilingComplete] = useState(false);
  const [memberRecipient, setMemberRecipient] = useState<any>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    loadDocuments();
    extractMemberRecipient();
  }, [taskId]);

  useEffect(() => {
    checkCompletionStatus();
  }, [documents]);

  useEffect(() => {
    // Check if task is already completed
    if (task?.status === 'completed') {
      setIsCompleted(true);
    }
  }, [task]);

  function extractMemberRecipient() {
    const workflowData = task.workflow_data || {};
    
    if (workflowData.member_full_name && workflowData.member_email) {
      setMemberRecipient({
        id: 'new_member',
        name: workflowData.member_full_name,
        email: workflowData.member_email,
        type: 'new_member'
      });
    }
  }

  async function loadDocuments() {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', taskId)
        .in('status', ['finalized', 'final'])
        .order('generated_at', { ascending: true });

      if (error) throw error;
      
      setDocuments(data || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function checkCompletionStatus() {
    await checkDistributionComplete();
    await checkFilingComplete();
  }

  async function checkDistributionComplete(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('document_distribution_log')
        .select('id, status')
        .eq('task_id', taskId);

      if (error) throw error;

      const isComplete = data && data.length > 0 && data.some(d => d.status === 'sent');
      setDistributionComplete(isComplete);
      return isComplete;
    } catch (error) {
      console.error('Error checking distribution status:', error);
      return false;
    }
  }

  async function checkFilingComplete(): Promise<boolean> {
    try {
      const { data: govDocs, error } = await supabase
        .from('governance_documents')
        .select('id')
        .eq('generated_from_task_id', taskId);

      if (error) throw error;

      // Check if at least one document has been filed
      const isComplete = govDocs && govDocs.length >= 1;
      
      setFilingComplete(isComplete);
      return isComplete;
    } catch (error) {
      console.error('Error checking filing status:', error);
      return false;
    }
  }

  async function handleDistributionComplete() {
    await checkDistributionComplete();
  }

  async function handleFilingComplete() {
    await checkFilingComplete();
  }

  async function handleMarkComplete() {
    try {
      setIsCompleting(true);
      
      const { error } = await supabase.functions.invoke('complete-member-appointment', {
        body: {
          taskId,
          unaId,
          memberData: task.workflow_data
        }
      });

      if (error) throw error;

      setIsCompleted(true);
      
      toast({
        title: 'Phase Complete',
        description: 'Member appointment process completed successfully',
      });

      onPhaseComplete?.();
    } catch (error: any) {
      console.error('Error completing phase:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete phase',
        variant: 'destructive',
      });
    } finally {
      setIsCompleting(false);
    }
  }

  // Distribution is optional, only filing is required
  const allComplete = filingComplete;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <CardTitle>Phase 3: Distribute & File Records</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              Final phase: distribute appointment documents to the new member and file records in governance archives.
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Distribute documents to new member via email</li>
              <li>File member appointment records in governance documents</li>
              <li>Complete member onboarding workflow</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phase 3 Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between p-2 border rounded">
            <span>Document Distribution</span>
            {distributionComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <span className="text-sm text-muted-foreground">Pending</span>
            )}
          </div>
          <div className="flex items-center justify-between p-2 border rounded">
            <span>Governance Filing</span>
            {filingComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <span className="text-sm text-muted-foreground">Pending</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Document Distribution */}
      <DocumentDistributionPanel
        taskId={taskId}
        unaId={unaId}
        documents={documents}
        additionalRecipients={memberRecipient ? [memberRecipient] : []}
        onDistributionComplete={handleDistributionComplete}
      />

      {/* Governance Filing */}
      <GovernanceFilingPanel
        taskId={taskId}
        unaId={unaId}
        documents={documents}
        workflowKey="add_member_simple"
        onFilingComplete={handleFilingComplete}
      />

      {/* Complete Phase - Show completion state or button */}
      {isCompleted ? (
        <Alert className="border-green-200 bg-green-50">
          <PartyPopper className="h-5 w-5 text-green-600" />
          <AlertDescription>
            <div className="ml-2">
              <p className="font-medium text-green-800">Member Appointment Completed</p>
              <p className="text-sm text-green-700">
                This workflow has been completed successfully. The new member has been added to your UNA.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className={!allComplete ? 'border-muted' : 'border-green-200 bg-green-50'}>
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {allComplete ? 'All Phase 3 Steps Complete' : 'Complete Member Appointment'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {allComplete 
                    ? `${distributionComplete ? 'Documents distributed and filed.' : 'Documents filed.'} Ready to complete workflow.`
                    : 'File at least one document to governance to complete. Distribution is optional.'
                  }
                </p>
              </div>
              <Button 
                onClick={handleMarkComplete}
                disabled={!allComplete || isCompleting}
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  'Complete Member Appointment'
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
